import { distinctValues } from './data.js';
import { typeColor } from './map.js';

/**
 * Build the filter UI and wire it to re-render on change.
 *
 * Filtering semantics: OR within a facet, AND across facets. An empty facet
 * (nothing checked) applies no constraint for that facet.
 *
 * @param {Object} opts
 * @param {HTMLElement} opts.container - element to render the filter panel into
 * @param {import('./data.js').Place[]} opts.places - full place list
 * @param {(filtered: import('./data.js').Place[]) => void} opts.onChange - called with the filtered list
 */
export function createFilters({ container, places, onChange }) {
  const names = distinctValues(places, 'name');
  const types = distinctValues(places, 'typeLabels');
  const suburbs = distinctValues(places, 'suburb');

  const selectedNames = new Set();
  const selectedTypes = new Set();
  const selectedSuburbs = new Set();

  function compute() {
    return places.filter((p) => {
      const nameOk = selectedNames.size === 0 || selectedNames.has(p.name);
      const typeOk =
        selectedTypes.size === 0 || p.typeLabels.some((t) => selectedTypes.has(t));
      const suburbOk = selectedSuburbs.size === 0 || selectedSuburbs.has(p.suburb);
      return nameOk && typeOk && suburbOk;
    });
  }

  function emit() {
    const filtered = compute();
    countEl.textContent = `Showing ${filtered.length} of ${places.length} places`;
    onChange(filtered);
  }

  // --- DOM construction ---
  container.innerHTML = '';

  const countEl = document.createElement('p');
  countEl.className = 'filters__count';

  const nameSection = buildSection('Name', names, selectedNames, emit);
  const typeSection = buildSection('Type', types, selectedTypes, emit, {
    swatch: (label) => typeColor(label.toLowerCase()),
  });
  const suburbSection = buildSection('Suburb', suburbs, selectedSuburbs, emit);

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'filters__clear';
  clearBtn.textContent = 'Clear all filters';
  clearBtn.addEventListener('click', () => {
    selectedNames.clear();
    selectedTypes.clear();
    selectedSuburbs.clear();
    container
      .querySelectorAll('input[type="checkbox"]')
      .forEach((cb) => (cb.checked = false));
    container.querySelectorAll('input[type="search"]').forEach((inp) => (inp.value = ''));
    // Re-show any options hidden by a per-section filter search.
    container.querySelectorAll('.filters__option').forEach((opt) => (opt.hidden = false));
    emit();
  });

  container.append(nameSection, countEl, typeSection, suburbSection, clearBtn);

  // Initial render (no filters → all places).
  emit();
}

/**
 * Build a labelled checkbox group for one facet.
 * @param {string} title
 * @param {string[]} values
 * @param {Set<string>} selectedSet - mutated as checkboxes toggle
 * @param {() => void} onToggle
 * @param {{ swatch?: (value: string) => string }} [opts]
 */
function buildSection(title, values, selectedSet, onToggle, opts = {}) {
  const section = document.createElement('section');
  section.className = 'filters__section';

  // The heading doubles as a button that collapses/expands the section body.
  const heading = document.createElement('button');
  heading.type = 'button';
  heading.className = 'filters__heading';
  heading.setAttribute('aria-expanded', 'true');
  const headingText = document.createElement('span');
  headingText.textContent = title;
  const chevron = document.createElement('span');
  chevron.className = 'filters__chevron';
  chevron.setAttribute('aria-hidden', 'true');
  heading.append(headingText, chevron);
  heading.addEventListener('click', () => {
    const collapsed = section.classList.toggle('filters__section--collapsed');
    heading.setAttribute('aria-expanded', String(!collapsed));
  });
  section.appendChild(heading);

  const body = document.createElement('div');
  body.className = 'filters__body';

  const list = document.createElement('div');
  list.className = 'filters__options';

  const optionEls = [];
  for (const value of values) {
    const label = document.createElement('label');
    label.className = 'filters__option';
    optionEls.push({ value, label });

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.value = value;
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) selectedSet.add(value);
      else selectedSet.delete(value);
      onToggle();
    });

    label.appendChild(checkbox);

    if (opts.swatch) {
      const swatch = document.createElement('span');
      swatch.className = 'filters__swatch';
      swatch.style.background = opts.swatch(value);
      label.appendChild(swatch);
    }

    const text = document.createElement('span');
    text.textContent = value;
    label.appendChild(text);

    list.appendChild(label);
  }

  // For long lists, add a text box that narrows the visible options by basic
  // substring matching. This only hides options; it never changes selection.
  if (values.length > 6) {
    const search = document.createElement('input');
    search.type = 'search';
    search.className = 'filters__search';
    search.placeholder = `Filter ${title.toLowerCase()}…`;
    search.setAttribute('aria-label', `Filter ${title.toLowerCase()} options`);
    search.addEventListener('input', () => {
      const q = search.value.trim().toLowerCase();
      for (const { value, label } of optionEls) {
        label.hidden = q !== '' && !value.toLowerCase().includes(q);
      }
    });
    body.appendChild(search);
  }

  body.appendChild(list);
  section.appendChild(body);
  return section;
}
